import React, { useState, useEffect } from 'react';
import { Container, Form, InputGroup, Alert } from 'react-bootstrap';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import './SubmitClaim.scss';
import LoaderButton from '../components/LoaderButton';
import AddressPicker from '../components/AddressPicker';
import TabbedContainer from '../components/TabbedContainer';
import { useAppContext } from '../libs/contextLib';
import { useFormFields } from '../libs/hooksLib';
import { UiError } from '../libs/errorLib';
import { useMatomo } from '@jonkoops/matomo-tracker-react';
import ReCAPTCHA from "react-google-recaptcha";
import config from '../config';
import {
  getPromiseFromApi,
} from '../libs/integralLib';

// Constants
import {
  claimsDocLink,
  nonRequiredFields,
  nonRequiredMainEmployed,
  nonRequiredFieldsDocChanged,
  nonRequiredFieldsAccountant1,
  nonRequiredFieldsOcc2nd,
  nonRequiredFieldsAccountant2,
  occupationStatusesWithAccountant,
  selectYesNo,
  selectOccupationalStatus,
  employSelectCondition,
  employSelectCondition3,
  selectRelationtoClient,
  selectTitlePrefix,
  selectBankAccount,
  reviewFields,
} from './SubmitClaimConstants';
import {
  origoList,
} from '../libs/origoLib';
import { rfc3339StringToDateString } from '../libs/stringLib'
import { origoCodeToOccupation } from '../libs/origoLib';

export default function SubmitClaim() {
  const { setError } = useAppContext();
  // We initialise to true so we don't start validating the form until the user
  // has clicked submit the first time. This prevents annoying messages from
  // showing until the user is 'done'.
  const [isValid, setIsValid] = useState(true);
  const [activePage, setActivePage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { trackEvent } = useMatomo();
  const [captchaValue, setCaptchaValue] = useState(null);
  /**
   * Need a data source to keep track of all fields for output
   * Start by adding ALL non required fields. Test these against the isEmpty helper function
   * Modify this Object with the requiredFields Object that is created during each TAB validation
   */
  const [requiredFieldsTAB2, setRequiredFieldsTAB2] = useState(["Your", "Claimant"]);
  const [requiredFieldsTAB3, setRequiredFieldsTAB3] = useState([]);
  const [requiredFieldsTAB4, setRequiredFieldsTAB4] = useState([]);
  const [requiredFieldsTAB5, setRequiredFieldsTAB5] = useState([]);
  const [requiredFieldsTAB6, setRequiredFieldsTAB6] = useState([]);
  const [fieldsWithoutHiddenElements, setFieldsWithoutHiddenElements] = useState({});
  const [isTemporarilyDown, setIsTemporarilyDown] = useState(false);

  const [fields, handleFieldChange] = useFormFields({
    areYouMember: '',
    formFilledOutByRelationship: '',
    formFilledOutByName: '',
    member_contractNumber: '',
    member_title: '',
    member_firstName: '',
    member_lastName: '',
    member_postcode: '',
    member_addressLine1: '',
    member_addressLine2: '',
    member_town: '',
    member_county: '',
    member_country: '',
    member_telephone: '',
    member_email: '',
    member_contactPreferencePhone: '',
    member_contactPreferenceEmail: '',
    member_contactPreferencePost: '',
    member_DOB: '',
    illnessInjury_what: '',
    illnessInjury_how: '',
    illnessInjury_prevent_working: '',
    illnessInjury_when: '',
    illnessInjury_where: '',
    illnessInjury_history: '',
    illnessInjury_consultation: '',
    doctor_firstName: '',
    doctor_lastName: '',
    doctor_telephone: '',
    doctor_postcode: '',
    doctor_addressLine1: '',
    doctor_addressLine2: '',
    doctor_town: '',
    doctor_county: '',
    doctor_country: '',
    doctor_changed: '',
    doctor_changed_firstName: '',
    doctor_changed_lastName: '',
    doctor_changed_telephone: '',
    doctor_changed_postcode: '',
    doctor_changed_addressLine1: '',
    doctor_changed_addressLine2: '',
    doctor_changed_town: '',
    doctor_changed_county: '',
    doctor_changed_country: '',
    illnessInjury_treatment: '',
    illnessInjury_hospitalised: '',
    occupation_main_origo: '',
    occupation_main_further: '',
    occupation_status: '',
    occupation_employer_name: '',
    occupation_employer_telephone: '',
    occupation_employer_email: '',
    occupation_postcode: '',
    occupation_addressLine1: '',
    occupation_addressLine2: '',
    occupation_town: '',
    occupation_county: '',
    occupation_further_accountantholdrecords: '',
    occupation_accountant_name: '',
    occupation_accountant_telephone: '',
    occupation_accountant_email: '',
    occupation_accountant_postcode: '',
    occupation_accountant_addressLine1: '',
    occupation_accountant_address_Line2: '',
    occupation_accountant_town: '',
    occupation_accountant_county: '',
    occupation_2nd: '',
    occupation_2nd_origo: '',
    occupation_2nd_further: '',
    occupation_2nd_status: '',
    occupation_2nd_name: '',
    occupation_2nd_telephone: '',
    occupation_2nd_email: '',
    occupation_2nd_postcode: '',
    occupation_2nd_addressLine1: '',
    occupation_2nd_addressLine2: '',
    occupation_2nd_town: '',
    occupation_2nd_county: '',
    occupation_2nd_further_accountantholdrecords: '',
    occupation_2nd_accountant_name: '',
    occupation_2nd_accountant_telephone: '',
    occupation_2nd_accountant_email: '',
    occupation_2nd_accountant_postcode: '',
    occupation_2nd_accountant_addressLine1: '',
    occupation_2nd_accountant_addressLine2: '',
    occupation_2nd_accountant_town: '',
    occupation_2nd_accountant_county: '',
    occupation_more: '',
    occupation_changed12Months: '',
    benefit_continuedEarningsSSP: '',
    benefit_EarlyRetirement: '',
    benefit_workPensions: '',
    benefit_otherInsurance: '',
    payments: '',
    furtherInformation: '',
    furtherInformation_more: '',
    discussClaimOthers: '',
    discussClaimOthers_more: '',
    confirmation: false
  });

  /**
   * Called after fields is updated in handleFieldChange.
   * Will validate form if state currently isn't valid.
   */
  useEffect(() => {
    if (!isValid) {
      // Don't scroll when any of these change, do update validation messages. 
      validateActivePage(false, true);
    }
  },
    // eslint-disable-next-line
    [isValid, fields, captchaValue]
  );

  useEffect(() => {
    const fieldsWithoutHiddenElements = getFieldsWithoutHiddenElements()
    setFieldsWithoutHiddenElements(fieldsWithoutHiddenElements);
  },
    // eslint-disable-next-line
    [
      requiredFieldsTAB2,
      requiredFieldsTAB3,
      requiredFieldsTAB4,
      requiredFieldsTAB5,
      requiredFieldsTAB6,
      fields
    ])

  /**
   * Uses the requiredFields state values to create a version of the formFields
   * data where non-visible form entries are set to null.
   */
  function getFieldsWithoutHiddenElements() {
    // The various requiredFields state objects contain all required fields in the
    // format
    // { <field id> : <value> }

    // The various nonRequiredFields consts contain fields that are not required
    // but may be hidden depending on form conditions.
    let localNonRequiredFields = nonRequiredFields;
    // These are visible if the claimant changed doctor.
    if (fields.doctor_changed === "Yes") {
      localNonRequiredFields = { ...localNonRequiredFields, ...nonRequiredFieldsDocChanged };
    }

    // Employer address only visible for 'employed'
    if (fields.occupation_status === "Employed") {
      localNonRequiredFields = { ...localNonRequiredFields, ...nonRequiredMainEmployed };
    }

    // For the claimant's accountant to be visible they need to have an
    // occupational status with an accountant, and have marked that an
    // accountant holds their records.
    if (
      occupationStatusesWithAccountant.includes(fields.occupation_status) &&
      fields.occupation_further_accountantholdrecords === "Yes"
    ) {
      localNonRequiredFields = { ...localNonRequiredFields, ...nonRequiredFieldsAccountant1 };
    }

    // Employer address 2 only visible for claimant with a second occupation
    // and status 'employed'
    if (
      fields.occupation_2nd === "Yes" &&
      fields.occupation_2nd_status === "Employed"
    ) {
      localNonRequiredFields = { ...localNonRequiredFields, ...nonRequiredFieldsOcc2nd };
    }

    // For the claimant's second accountant to be visible they need to have a
    // second occupation, a 2nd occupational status with an accountant, and
    // have marked that an accountant holds their records.
    if (
      fields.occupation_2nd === "Yes" &&
      occupationStatusesWithAccountant.includes(fields.occupation_2nd_status) &&
      fields.occupation_2nd_further_accountantholdrecords === "Yes"
    ) {
      localNonRequiredFields = { ...localNonRequiredFields, ...nonRequiredFieldsAccountant2 };
    }

    const visibleFields = {
      ...localNonRequiredFields,
      ...requiredFieldsTAB2,
      ...requiredFieldsTAB3,
      ...requiredFieldsTAB4,
      ...requiredFieldsTAB5,
      ...requiredFieldsTAB6
    };
    let result = {};
    // Go voer localFields, and blank out any value that does not have a key in visibleFields.
    Object.keys(fields).forEach(key => {
      if (!visibleFields.hasOwnProperty(key)) {
        result[key] = null;
      }
      else {
        result[key] = fields[key];
      }
    });
    return (result);
  }
  /**
   * Selects formatting helper function based on format parameter passed to it
   * Returns formatted value
   * If no format passed in then just returns unchanged value 
   */
  function formatValue(value, format) {
    switch (format) {
      case "date":
        return rfc3339StringToDateString(value);
      case "origo":
        return origoCodeToOccupation(value);
      default:
        return value;
    }
  }

  /**
   * Returns all visible fields from the form with their content for review by
   * the user.
   */
  function getFormDataForReview() {
    return reviewFields.map((reviewField) => {
      if (!fieldsWithoutHiddenElements.hasOwnProperty(reviewField.id) || fieldsWithoutHiddenElements[reviewField.id] === null) {
        return (null);
      }
      return (
        <li key={reviewField.id}>
          {reviewField.question}
          <br />
          <strong>
            {formatValue(fields[reviewField.id], reviewField.format)}
          </strong>
        </li>
      )
    })
  }

  /**
   * Displays answers back to submitter
   */
  function displayAnswersBack() {
    //const arrayDataItems = fields.map((fields.arguments) => <li>{fields.arguments}</li>);
    return (
      <div class="bg-success-subtle p-4">
        <h1>Review and Submit</h1>
        <p>Please review your answers before submitting as no change can be made to the form following submission.
          If you need to amend, please use the 'previous' button or click on the tabs along the top to return to the questions you'd like to amend.</p>
        {getFormDataForReview()}
      </div>
    );
  }
  /**
   * Calls the validaiton for the active page.
   * @param scrollToTopOnInvalid whether to scroll to the top of the page when
   *                             validaiton fails.
   * @param showValidationErrors whether to show validation errors or validate
   *                             silently.
   */
  function validateActivePage(
    scrollToTopOnInvalid = false,
    showValidationErrors = true
  ) {
    let Validate = true;
    if (Validate) {
      let isValid = false;
      switch (activePage) {
        case 0:
          isValid = validateFormPageOne(showValidationErrors);
          break;
        case 1:
          isValid = validateFormPageTwo(showValidationErrors);
          break;
        case 2:
          isValid = validateFormPageThree(showValidationErrors);
          break;
        case 3:
          isValid = validateFormPageFour(showValidationErrors);
          break;
        case 4:
          isValid = validateFormPageFive(showValidationErrors);
          break;
        case 5:
          isValid = validateFormPageSix(showValidationErrors);
          break;
        case 6:
          isValid = validateFormPageSeven(showValidationErrors);
          break;
        default:
          isValid = false;
          break
      }
      if (!isValid && scrollToTopOnInvalid) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return (isValid);
    } else {
      return (true);
    }
  }

  /**
   * Validates page one of the form, sets error messages and isValid accordingly.
   * @param showValidationErrors whether to show validation errors or validate
   *                             silently.
   */
  function validateFormPageOne(showValidationErrors = true) {
    const valid = true;
    setIsValid(valid);
    return valid;
  }

  /**
   * Validates page two of the form, sets error messages and isValid accordingly.
   * @param showValidationErrors whether to show validation errors or validate
   *                             silently.
   */
  function validateFormPageTwo(showValidationErrors = true) {
    let valid = true;

    let requiredFields = {
      'areYouMember': 'Are you the member?',
      'member_title': 'Title',
      'member_firstName': 'First Name',
      'member_lastName': 'Last Name',
      'member_postcode': 'Postcode',
      'member_addressLine1': 'Address Line 1',
      'member_town': 'Town',
      'member_county': 'County',
      'member_telephone': 'Phone',
      'member_email': 'Email',
      'member_contactPreferencePhone': 'Contact by Phone',
      'member_contactPreferenceEmail': 'Contact by Email',
      'member_contactPreferencePost': 'Contact by Post',
      'member_DOB': 'Date of Birth'
    };

    let requiredFieldsNotMember = {
      'formFilledOutByRelationship': 'Relationship to member',
      'formFilledOutByName': 'Full Name'
    };

    if (fields.areYouMember === "No") {
      requiredFields = { ...requiredFields, ...requiredFieldsNotMember };
    }

    let messages = [];

    // Build a message about unfilled required fields.
    let unfilledFields = [];

    Object.keys(requiredFields).forEach(key => {
      checkEmpty(key, requiredFields[key])
    });

    function checkEmpty(key, value) {
      let checkItem = fields[key];
      if ((typeof checkItem === 'string' || checkItem instanceof String) && checkItem.trim().length === 0) {
        unfilledFields.push(value);
      }
    }

    // if (
    //   !uktelRegex.test(fields.member_telephone)
    // ) {
    //   valid = false;
    //   messages.push('Please ensure the phone number is in the correct format.');
    // }


    // if (
    //   !emailRegex.test(fields.member_email)
    // ) {
    //   valid = false;
    //   messages.push('Please ensure the email address is in the correct format.');
    // }
    /**
     * Convert the contact preference fields to numbers
     * return sum
     */
    function checkContactPrefs(){
      const a = fields.member_contactPreferenceEmail === "Yes" ? 1 : 0;
      const b = fields.member_contactPreferencePhone === "Yes" ? 1 : 0;
      const c = fields.member_contactPreferencePost === "Yes" ? 1 : 0;
      var sum = a + b + c;
      return sum;
    }

    if (checkContactPrefs() < 1){
      valid = false;
      messages.push('Please make sure one of the contact preferences are set to \'Yes\'.');
    }

    /**
     * Check date returns true or false. no future dates allowed
     */
    function checkDate(date) {
      let dateValid = false;
      const selected = new Date(date);
      const maxDate = new Date();
      maxDate.setHours(0, 0, 0, 0);
      maxDate.setDate(maxDate.getDate() + 1);

      if (!isNaN(new Date(date)) && (selected < maxDate)) {
        dateValid = true;
      }

      return dateValid;
    }

    if (!checkDate(fields.member_DOB)) {
      valid = false;
      messages.push('Please ensure the date is in the correct format and not a future date.');
    }

    if (unfilledFields.length > 0) {
      messages.push(`Please ensure the ${unfilledFields.join(', ')} ${unfilledFields.length > 1 ? 'fields are' : 'field is'} filled.`);
      valid = false;
    }

    if (showValidationErrors) {
      // Potentially clear errors.
      if (valid) {
        setError(
          new UiError('submitClaim', '', 'resolved')
        );
      }
      else {
        // Build an error message.
        const errorElement = <div>
          {
            messages.map((message) => {
              return (<><span>{message}</span><br /></>);
            })
          }
        </div>
        setError(
          new UiError(
            'submitClaim',
            errorElement
          )
        );
      }
    }
    setIsValid(valid);
    /**
     * build an object ready for Output
     */
    setRequiredFieldsTAB2(requiredFields);
    //console.dir(requiredFieldsTAB2);
    return valid;
  }

  /**
   * Validates page three of the form, sets error messages and isValid accordingly.
   * @param showValidationErrors whether to show validation errors or validate
   *                             silently.
   */
  function validateFormPageThree(showValidationErrors = true) {
    let valid = true;

    let requiredFields = {
      'illnessInjury_what': 'What is the illness / injury?',
      'illnessInjury_how': 'How did it happen / occur?',
      'illnessInjury_prevent_working': 'What symptoms have prevented you from performing your occupation?',
      'illnessInjury_when': 'When did your illness /injury commence?',
      'illnessInjury_where': 'Geographical location injury / illness occurred',
      'illnessInjury_history': 'Have you ever suffered from the illness or injury before?',
      'illnessInjury_consultation': 'Have you consulted a doctor / specialist?',
      'doctor_firstName': 'Your Doctor\'s First Name',
      'doctor_lastName': 'Your Doctor\'s Last Name',
      'doctor_telephone': 'Your Doctor\'s Telephone',
      'doctor_postcode': 'Your Doctor\'s Postcode',
      'doctor_addressLine1': 'Your Doctor\'s Address Line 1',
      'doctor_town': 'Your Doctor\'s Town',
      'doctor_county': 'Your Doctor\'s County',
      'doctor_changed': 'Have you changed your doctor within the last 12 months?',
      'illnessInjury_treatment': 'What treatment/medication have you received and do you anticipate further treatment?',
      'illnessInjury_hospitalised': 'Have you been hospitalised in result of your injury / illness?'
    };

    let requiredFieldsPreviousDoctor = {
      'doctor_changed_firstName': 'Please provide previous Doctor\'s First Name',
      'doctor_changed_lastName': 'Please provide previous Doctor\'s Last Name',
      'doctor_changed_telephone': 'Please provide previous Doctor\'s Telephone',
      'doctor_changed_postcode': 'Please provide previous Doctor\'s Postcode',
      'doctor_changed_addressLine1': 'Please provide first line of previous Doctor\'s address',
      'doctor_changed_town': 'Please provide previous Doctor\'s town',
      'doctor_changed_county': 'Please provide previous Doctor\'s county'
    }

    if (fields.doctor_changed === "Yes") {
      requiredFields = { ...requiredFields, ...requiredFieldsPreviousDoctor };
    }

    let messages = [];

    // if (
    //   !uktelRegex.test(fields.doctor_telephone)
    // ) {
    //   valid = false;
    //   messages.push('Please ensure the doctor\'s phone number is in the correct format.');
    // }

    /**
 * Check date returns true or false. no future dates allowed
 */
    function checkDate(date) {
      let dateValid = false;
      const selected = new Date(date);
      const maxDate = new Date();
      maxDate.setHours(0, 0, 0, 0);
      maxDate.setDate(maxDate.getDate() + 1);

      if (!isNaN(new Date(date)) && (selected < maxDate)) {
        dateValid = true;
      }

      return dateValid;
    }

    if (!checkDate(fields.illnessInjury_when)) {
      valid = false;
      messages.push('Please ensure the date is in the correct format and not a future date.');
    }


    // if ("doctor_changed_telephone" in requiredFields) {
    //   if (
    //     !uktelRegex.test(fields.doctor_changed_telephone)
    //   ) {
    //     valid = false;
    //     messages.push('Please ensure the previous doctor\'s phone number is in the correct format.');
    //   }
    // }

    // Build a message about unfilled required fields.
    let unfilledFields = [];

    Object.keys(requiredFields).forEach(key => {
      checkEmpty(key, requiredFields[key])
    });

    function checkEmpty(key, value) {
      let checkItem = fields[key];
      if ((typeof checkItem === 'string' || checkItem instanceof String) && checkItem.trim().length === 0) {
        unfilledFields.push(value);
      }
    }


    if (unfilledFields.length > 0) {
      messages.push(`Please ensure the ${unfilledFields.join(', ')} ${unfilledFields.length > 1 ? 'fields are' : 'field is'} filled.`);
      valid = false;
    }

    if (showValidationErrors) {
      // Potentially clear errors.
      if (valid) {
        setError(
          new UiError('submitClaim', '', 'resolved')
        );
      }
      else {
        // Build an error message.
        const errorElement = <div>
          {
            messages.map((message) => {
              return (<><span>{message}</span><br /></>);
            })
          }
        </div>
        setError(
          new UiError(
            'submitClaim',
            errorElement
          )
        );
      }
    }
    setIsValid(valid);
    /**
     * build an object ready for Output
     */
    setRequiredFieldsTAB3(requiredFields);
    return valid;
  }

  /**
   * Validates page four of the form, sets error messages and isValid accordingly.
   * @param showValidationErrors whether to show validation errors or validate
   *                             silently.
   */
  function validateFormPageFour(showValidationErrors = true) {
    let valid = true;

    let requiredFields = {
      'occupation_main_origo': 'What is your main occupation',
      'occupation_status': 'Occupation status',
      'occupation_2nd': 'Do you have a secondary occupation?',
      'occupation_changed12Months': 'Have your employment circumstances changed in the last 12 months?'
    };

    let requiredFieldsEmployed = {
      'occupation_employer_name': 'Employer Name',
      'occupation_employer_telephone': 'Employer Telephone Number',
      'occupation_employer_email': 'Employer Email',
      'occupation_postcode': 'Please provide Postcode',
      'occupation_addressLine1': 'Please provide first line of address',
      'occupation_town': 'Please provide town',
      'occupation_county': 'Please provide county'
    }

    let requiredFieldsSecondary = {
      'occupation_2nd_origo': 'What is your secondary occupation',
      'occupation_2nd_status': 'Secondary Occupation status',
      'occupation_more': 'Do you have more than two occupations'
    }

    let requiredFieldsSecondaryEmployed = {
      'occupation_2nd_name': 'Second Employer Name',
      'occupation_2nd_telephone': 'Second Employer Telephone Number',
      'occupation_2nd_email': 'Second Employer Email',
      'occupation_2nd_postcode': 'Please provide Second Employer Postcode',
      'occupation_2nd_addressLine1': 'Please provide Second Employer first line of address',
      'occupation_2nd_town': 'Please provide Second Employer town',
      'occupation_2nd_county': 'Please provide Second Employer county'
    }

    let requiredFieldsFinancialRecords = {
      'occupation_further_accountantholdrecords': 'Does your accountant hold your financial records?'
    }

    let requiredFieldsAccountant = {
      'occupation_accountant_name': 'Please provide Accountant Name',
      'occupation_accountant_telephone': 'Please provide Accountant Telephone',
      'occupation_accountant_email': 'Please provide Accountant Email',
      'occupation_accountant_postcode': 'Please provide Accountant Postcode',
      'occupation_accountant_addressLine1': 'Please provide first line of Accountant\'s address',
      'occupation_accountant_town': 'Please provide Accountant\'s town',
      'occupation_accountant_county': 'Please provide Accountant\'s county'
    }

    let requiredFieldsFinancialRecords2 = {
      'occupation_2nd_further_accountantholdrecords': 'Does your accountant hold your financial records?'
    }

    let requiredFieldsAccountant2 = {
      'occupation_2nd_accountant_name': 'Please provide Accountant Name for secondary employment',
      'occupation_2nd_accountant_telephone': 'Please provide Accountant Telephone for secondary employment',
      'occupation_2nd_accountant_email': 'Please provide Accountant Email for secondary employment',
      'occupation_2nd_accountant_postcode': 'Please provide Accountant Postcode for secondary employment',
      'occupation_2nd_accountant_addressLine1': 'Please provide first line of Accountant\'s address for secondary employment',
      'occupation_2nd_accountant_town': 'Please provide Accountant\'s town for secondary employment',
      'occupation_2nd_accountant_county': 'Please provide Accountant\'s county for secondary employment'
    }

    /**
     * occupation main add employed required fields
     */
    if (employSelectCondition3.indexOf(fields.occupation_status) > -1) {
      requiredFields = { ...requiredFields, ...requiredFieldsEmployed };
    }

    /**
     * occupation main add financial records becasue special employed required fields
     */
    if (employSelectCondition.indexOf(fields.occupation_status) > -1) {
      requiredFields = { ...requiredFields, ...requiredFieldsFinancialRecords };
    }

    /**
     * if accounts for main employment are kept with accountant make accountant address required 
     */
    if ((fields.occupation_further_accountantholdrecords === "Yes") && (employSelectCondition.indexOf(fields.occupation_status) > -1)) {
      requiredFields = { ...requiredFields, ...requiredFieldsAccountant };
    }

    /**
     * If they answer yes to secondary occupation then we need more details make those required fields
     */
    if (fields.occupation_2nd === "Yes") {
      requiredFields = { ...requiredFields, ...requiredFieldsSecondary };
    }


    /**
     * occupation 2nd add employed required fields
     */
    if ((employSelectCondition3.indexOf(fields.occupation_2nd_status) > -1) && (fields.occupation_2nd === "Yes")) {
      requiredFields = { ...requiredFields, ...requiredFieldsSecondaryEmployed };
    }

    /**
    * occupation 2nd add financial records becasue special employed required fields
    */
    if (employSelectCondition.indexOf(fields.occupation_2nd_status) > -1) {
      requiredFields = { ...requiredFields, ...requiredFieldsFinancialRecords2 };
    }

    /**
     * if accounts for 2nd employment are kept with accountant make accountant address required 
     */
    if ((fields.occupation_2nd_further_accountantholdrecords === "Yes") && (employSelectCondition.indexOf(fields.occupation_2nd_status) > -1)) {
      requiredFields = { ...requiredFields, ...requiredFieldsAccountant2 };
    }

    let messages = [];

    /**
     * If employer telephone in required fields validate it as telephone number
     */
    // if ("occupation_employer_telephone" in requiredFields) {
    //   if (
    //     !uktelRegex.test(fields.occupation_employer_telephone)
    //   ) {
    //     valid = false;
    //     messages.push('Please ensure employer phone number is in the correct format.');
    //   }
    // }

    /**
     * If 2nd employment telephone in required fields validate it as telephone
     */
    // if ("occupation_2nd_telephone" in requiredFields) {
    //   if (
    //     !uktelRegex.test(fields.occupation_2nd_telephone)
    //   ) {
    //     valid = false;
    //     messages.push('Please ensure second employer phone number is in the correct format.');
    //   }
    // }

    // if ("occupation_employer_email" in requiredFields) {
    //   if (
    //     !emailRegex.test(fields.occupation_employer_email)
    //   ) {
    //     valid = false;
    //     messages.push('Please ensure the email address is in the correct format.');
    //   }
    // }

    // if ("occupation_2nd_email" in requiredFields) {
    //   if (
    //     !emailRegex.test(fields.occupation_2nd_email)
    //   ) {
    //     valid = false;
    //     messages.push('Please ensure the email address is in the correct format.');
    //   }
    // }

    /**
     * If accountant telephone in required fields validate it as telephone number
     */

    // if ("occupation_accountant_telephone" in requiredFields) {
    //   if (
    //     !uktelRegex.test(fields.occupation_accountant_telephone)
    //   ) {
    //     valid = false;
    //     messages.push('Please ensure accountant phone number is in the correct format.');
    //   }
    // }

    /**
     * If 2nd accountant telephone in required fields validate it as telephone number
     */

    // if ("occupation_2nd_accountant_telephone" in requiredFields) {
    //   if (
    //     !uktelRegex.test(fields.occupation_2nd_accountant_telephone)
    //   ) {
    //     valid = false;
    //     messages.push('Please ensure second accountant phone number is in the correct format.');
    //   }
    // }


    // if ("occupation_accountant_email" in requiredFields) {
    //   if (
    //     !emailRegex.test(fields.occupation_accountant_email)
    //   ) {
    //     valid = false;
    //     messages.push('Please ensure the accountant email address is in the correct format.');
    //   }
    // }

    // if ("occupation_2nd_accountant_email" in requiredFields) {
    //   if (
    //     !emailRegex.test(fields.occupation_2nd_accountant_email)
    //   ) {
    //     valid = false;
    //     messages.push('Please ensure the accountant email address is in the correct format.');
    //   }
    // }

    // Build a message about unfilled required fields.
    let unfilledFields = [];

    Object.keys(requiredFields).forEach(key => {
      checkEmpty(key, requiredFields[key])
    });

    function checkEmpty(key, value) {
      let checkItem = fields[key];
      if ((typeof checkItem === 'string' || checkItem instanceof String) && checkItem.trim().length === 0) {
        unfilledFields.push(value);
      }
    }


    if (unfilledFields.length > 0) {
      messages.push(`Please ensure the ${unfilledFields.join(', ')} ${unfilledFields.length > 1 ? 'fields are' : 'field is'} filled.`);
      valid = false;
    }

    if (showValidationErrors) {
      // Potentially clear errors.
      if (valid) {
        setError(
          new UiError('submitClaim', '', 'resolved')
        );
      }
      else {
        // Build an error message.
        const errorElement = <div>
          {
            messages.map((message) => {
              return (<><span>{message}</span><br /></>);
            })
          }
        </div>
        setError(
          new UiError(
            'submitClaim',
            errorElement
          )
        );
      }
    }
    setIsValid(valid);
    /**
     * build an object ready for Output
     */
    setRequiredFieldsTAB4(requiredFields);
    return valid;
  }

  /**
   * Validates page five of the form, sets error messages and isValid accordingly.
   * @param showValidationErrors whether to show validation errors or validate
   *                             silently.
   */
  function validateFormPageFive(showValidationErrors = true) {
    let valid = true;

    let requiredFields = {
      'benefit_continuedEarningsSSP': 'Continued Earnings including Statutory Sick Pay',
      'benefit_EarlyRetirement': 'Early retirement pension',
      'benefit_workPensions': 'Regular Government financial support?',
      'benefit_otherInsurance': 'Regular payments from other insurance '
    };

    let messages = [];

    // Build a message about unfilled required fields.
    let unfilledFields = [];

    Object.keys(requiredFields).forEach(key => {
      checkEmpty(key, requiredFields[key])
    });

    function checkEmpty(key, value) {
      let checkItem = fields[key];
      if ((typeof checkItem === 'string' || checkItem instanceof String) && checkItem.trim().length === 0) {
        unfilledFields.push(value);
      }
    }


    if (unfilledFields.length > 0) {
      messages.push(`Please ensure the ${unfilledFields.join(', ')} ${unfilledFields.length > 1 ? 'fields are' : 'field is'} filled.`);
      valid = false;
    }

    if (showValidationErrors) {
      // Potentially clear errors.
      if (valid) {
        setError(
          new UiError('submitClaim', '', 'resolved')
        );
      }
      else {
        // Build an error message.
        const errorElement = <div>
          {
            messages.map((message) => {
              return (<><span>{message}</span><br /></>);
            })
          }
        </div>
        setError(
          new UiError(
            'submitClaim',
            errorElement
          )
        );
      }
    }
    setIsValid(valid);
    /**
     * build an object ready for Output
     */
    setRequiredFieldsTAB5(requiredFields);
    return valid;
  }


  /**
   * Validates page six of the form, sets error messages and isValid accordingly.
   * @param showValidationErrors whether to show validation errors or validate
   *                             silently.
   */
  function validateFormPageSix(showValidationErrors = true) {
    let valid = true;
    //let requiredFields = ['payments','furtherInformation','discussClaimOthers','vulnerable'];
    let requiredFields = {
      'payments': 'Payments',
      'furtherInformation': 'Further Information',
      'discussClaimOthers': 'Discuss Claim with Others'
    };

    const requiredFieldsDiscussClaimOthers = {
      'discussClaimOthers_more': 'Third party details',
    }

    if (fields.discussClaimOthers === "Yes") {
      requiredFields = { ...requiredFields, ...requiredFieldsDiscussClaimOthers }
    }

    const requiredFieldsfurtherInformation = {
      'furtherInformation_more': 'Further Information',
    }

    if (fields.furtherInformation === "Yes") {
      requiredFields = { ...requiredFields, ...requiredFieldsfurtherInformation }
    }

    let messages = [];

    // Build a message about unfilled required fields.
    let unfilledFields = [];

    Object.keys(requiredFields).forEach(key => {
      checkEmpty(key, requiredFields[key])
    });

    function checkEmpty(key, value) {
      let checkItem = fields[key];
      if ((typeof checkItem === 'string' || checkItem instanceof String) && checkItem.trim().length === 0) {
        unfilledFields.push(value);
      }
    }


    if (unfilledFields.length > 0) {
      messages.push(`Please ensure the ${unfilledFields.join(', ')} ${unfilledFields.length > 1 ? 'fields are' : 'field is'} filled.`);
      valid = false;
    }

    if (showValidationErrors) {
      // Potentially clear errors.
      if (valid) {
        setError(
          new UiError('submitClaim', '', 'resolved')
        );
      }
      else {
        // Build an error message.
        const errorElement = <div>
          {
            messages.map((message) => {
              return (<><span>{message}</span><br /></>);
            })
          }
        </div>
        setError(
          new UiError(
            'submitClaim',
            errorElement
          )
        );
      }
    }
    setIsValid(valid);
    /**
     * build an object ready for Output
     */
    setRequiredFieldsTAB6(requiredFields);
    return valid;
  }

  /**
   * Validates page seven of the form, sets error messages and isValid accordingly.
   * @param showValidationErrors whether to show validation errors or validate
   *                             silently.
   */
  function validateFormPageSeven(showValidationErrors = true) {
    let valid = true;
    let messages = [];

    if (captchaValue === null) {
      valid = false;
      messages.push('Please solve the captcha.');
    }

    if (fields.confirmation === false) {
      valid = false;
      messages.push('Please confirm the answers are true and accurate.');
    }

    if (showValidationErrors) {
      // Potentially clear errors.
      if (valid) {
        setError(
          new UiError('submitClaim', '', 'resolved')
        );
      }
      else {

        // Build an error message.
        const errorElement = <div>
          {
            messages.map((message) => {
              return (<><span>{message}</span><br /></>);
            })
          }
        </div>
        setError(
          new UiError(
            'submitClaim',
            errorElement
          )
        );
      }
    }
    setIsValid(valid);
    return valid;
  }

  /**
   * Function to handle the user hitting submit.
   * Registers the user and sets the user in the context.
   * @param {event} event 
   */
  async function handleSubmit(event) {
    event.preventDefault();
    if (!validateActivePage(true, true)) {
      return;
    }

    setIsLoading(true);
    // Track the event.
    trackEvent({
      category: 'submitClaim',
      action: 'submit',
      name: 'submit form',
    });
    // Post the data.
    getPromiseFromApi(
      `${config.api.address}/formdata`,
      'PUT',
      {
        type: 2,
        data: fieldsWithoutHiddenElements
      }
    )
      .then(
        ({ code, data }) => {
          if (code === 200) {
            setError(
              new UiError('submitClaim', '', 'resolved')
            );
            setIsSubmitted(true);
          }
          else {
            setError(
              new UiError('submitClaim', data.error.message, "danger")
            );
          }
        }
      )
      .catch(
        message => {
          setError(
            new UiError('submitClaim', message)
          );
        }
      )
      .finally(
        () => {
          setIsLoading(false);
        }
      )
  }

  /**
   * Returns captcha if it's enabled.
   */
  function getCaptcha() {
    if (!config.captcha.enabled) {
      return (null);
    }
    return (
      <div className="recaptchaContainer">
        <ReCAPTCHA
          sitekey={config.captcha.siteKey}
          onChange={
            (value) => {
              setCaptchaValue(value)
            }
          }
          onExpired={() => setCaptchaValue(null)}
        />
      </div>
    )
  }

  /**
   * Returns the request form.
   */
  function getFormPageOne() {
    return (
      <div className="container mt-4 px-0">
        <h2>Page 1 of 7</h2>
        <div>
          <h3>Understanding your cover</h3>
          <p>It's important that you know what cover you have with us, please review your contract documentation and check the below before you claim.</p>
          <ul>
            <li>Do you have a deferred period? - The duration of time before benefit payments start to be made is called a deferred period.</li>
            <li>Have you accepted any medical exclusions? - This relates to a medical condition not covered within your contract. This could mean your claim may not be payable if the injury or incapacity relates to this medical exclusion.</li>
            <li>Do you know your level of cover? - This is the sum which will provide financial protection, it's important to know the maximum we can pay you if your claim is successful.</li>
          </ul>
          <p>Please ensure that when providing us with your claim details or sending us additional documentation that you provide clear and accurate information. Any inaccurate or false information may impact our ability to pay your claim.</p>
          <p>For further useful information regarding the claims journey please read <a target="_blank" rel="noreferrer" href={claimsDocLink}>Our guide to claims</a></p>
          <h3>What you need to complete this form</h3>
          <p>Once started, this form cannot be saved, so please make sure you have the following information to hand before starting:</p>
          <ul>
            <li>Your past and present GP details, we may need medical information and having these details will help speed up the process</li>
            <li>Your employer or accountant contact details</li>
            <li>Dates of when the illness/injury commenced</li>
            <li>Details of medication/treatment you have received</li>
          </ul>
        </div>
      </div>
    )
  }

  function getFormPageTwo() {
    return (
      <div className="container mt-4 px-0">
        <h2>Page 2 of 7</h2>
        <div>
          <Form>
            <div className="d-grid gap-2">
              <h3 className="mb-2 font-weight-normal text-center">Claim Submitter</h3>
              <div>
                <p>If you're filling out this form on behalf of someone else, please fill out the following sections as if you're the member.</p>
                <div class="form-floating">
                  <Form.Select
                    className="form-select required"
                    id="areYouMember"
                    value={fields.areYouMember}
                    onChange={handleFieldChange}
                    required
                  >
                    {selectYesNo.map(keypairs => (
                      <option value={keypairs.id}>{keypairs.value}</option>
                    ))}
                  </Form.Select>
                  <Form.Label htmlFor="areYouMember" className="form-label required">Are you the member? *</Form.Label>
                </div>

                {fields.areYouMember === "No" ? (

                  <div>
                    <div class="form-floating mt-2">
                      <Form.Select
                        className="required"
                        id="formFilledOutByRelationship"
                        value={fields.formFilledOutByRelationship}
                        onChange={handleFieldChange}
                        required
                      >
                        {selectRelationtoClient.map(keypairs => (
                          <option value={keypairs.id}>{keypairs.value}</option>
                        ))}
                      </Form.Select>
                      <Form.Label htmlFor="formFilledOutByRelationship" className="form-label">Please select your relationship with member below? *</Form.Label>
                    </div>

                    <div class="form-floating mt-2">
                      <Form.Control
                        className="input"
                        id="formFilledOutByName"
                        placeholder="What is your full name? *"
                        type="text"
                        autoComplete="fname"
                        value={fields.formFilledOutByName}
                        onChange={handleFieldChange}
                        required
                      />
                      <Form.Label htmlFor="formFilledOutByName" className="form-label">What is your full name? *</Form.Label>
                    </div>
                  </div>
                ) : null}

                <div>
                  <h3 className="mt-2 mb-2 font-weight-normal text-center">
                    {fields.areYouMember === "Yes" ? "Your" : "Claimant's"} Details
                  </h3>
                  <div class="form-floating">
                    <Form.Control
                      className="input"
                      id="member_contractNumber"
                      placeholder="What is your contract number?"
                      type="text"
                      value={fields.member_contractNumber}
                      onChange={handleFieldChange}
                      aria-describedby="helpContractNumber"
                    />
                    <Form.Label htmlFor="member_contractNumber" className="form-label">Contract number if known</Form.Label>
                    <Form.Text id="helpContractNumber" muted>
                      <i>The contract number is the unique reference given to your plan at commencement.</i>
                    </Form.Text>
                  </div>
                </div>

                <Row className="mt-2">
                  <Col lg={3}>
                    <div class="form-floating">
                      <Form.Select
                        className="input required"
                        id="member_title"
                        value={fields.member_title}
                        onChange={handleFieldChange}
                        required
                      >
                        {selectTitlePrefix.map(keypairs => (
                          <option value={keypairs.id}>{keypairs.value}</option>
                        ))}
                      </Form.Select>
                      <Form.Label htmlFor="member_title" className="form-label">What is your title? *</Form.Label>
                    </div>
                  </Col>
                  <Col>
                    <div class="form-floating">
                      <Form.Control
                        className="input required"
                        id="member_firstName"
                        placeholder="First name*"
                        type="text"
                        autoComplete="fname"
                        value={fields.member_firstName}
                        onChange={handleFieldChange}
                        required
                      />
                      <Form.Label htmlFor="member_firstName" className="form-label">First name *</Form.Label>
                    </div>
                  </Col>
                  <Col>
                    <div class="form-floating">
                      <Form.Control
                        id="member_lastName"
                        className="input"
                        placeholder="Last name*"
                        type="text"
                        autoComplete="lname"
                        value={fields.member_lastName}
                        onChange={handleFieldChange}
                        required
                      />
                      <Form.Label htmlFor="member_lastName" className="form-label">Last name *</Form.Label>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </Form>

          <div className="d-grid gap-2">
            <AddressPicker
              formTitle={`${fields.areYouMember === "Yes" ? "Your" : "Claimant's"} Address`}
              id="member_CorrespondenceAddress"
              className="mb-2"
              handleFieldChange={handleFieldChange}
              postcode={fields.member_postcode}
              addressLine1={fields.member_addressLine1}
              addressLine2={fields.member_addressLine2}
              addressTown={fields.member_town}
              addressCounty={fields.member_county}
              addressCountry={fields.member_country}
              postcodeFieldId='member_postcode'
              addressLine1FieldId='member_addressLine1'
              addressLine2FieldId='member_addressLine2'
              addressTownFieldId='member_town'
              addressCountyFieldId='member_county'
              addressCountryFieldId='member_country'
              oldLayout={false}
            />
          </div>
        </div>
        <div>
          <InputGroup>
            <div class="form-floating">
              <Form.Control
                id="member_email"
                className="input required"
                placeholder="Email Address*"
                type="email"
                autoComplete="username"
                value={fields.member_email}
                onChange={handleFieldChange}
                required
              />
              <Form.Label htmlFor="email" className="form-label">Email Address *</Form.Label>
            </div>
            <div class="form-floating">
              <Form.Control
                id="member_telephone"
                className="input required"
                placeholder="Phone*"
                type="tel"
                autoComplete="tel"
                value={fields.member_telephone}
                onChange={handleFieldChange}
                required
              />
              <Form.Label htmlFor="mobile" className="form-label">Phone *</Form.Label>
            </div>
          </InputGroup>

          <div class="mt-3">
            <p>What are your contact preferences?<br />
              <Form.Text id="helpContactPreference" muted>
                <i>This is your preferred method of how we communicate with you throughout your claim</i>
              </Form.Text>
            </p>
          </div>
          <Row>
            <Col>
              <div class="form-floating">
                <Form.Select
                  className="form-select required"
                  id="member_contactPreferencePhone"
                  value={fields.member_contactPreferencePhone}
                  onChange={handleFieldChange}
                  required
                >
                  {selectYesNo.map(keypairs => (
                    <option value={keypairs.id}>{keypairs.value}</option>
                  ))}
                </Form.Select>
                <Form.Label htmlFor="member_contactPreferencePhone" className="form-label ">Phone *</Form.Label>
              </div>
            </Col>
            <Col>
              <div class="form-floating">
                <Form.Select
                  className="form-select"
                  id="member_contactPreferenceEmail"
                  value={fields.member_contactPreferenceEmail}
                  onChange={handleFieldChange}
                  required
                >
                  {selectYesNo.map(keypairs => (
                    <option value={keypairs.id}>{keypairs.value}</option>
                  ))}
                </Form.Select>
                <Form.Label htmlFor="member_contactPreferenceEmail" className="form-label ">Email *</Form.Label>
              </div>

            </Col>
            <Col>
              <div class="form-floating">
                <Form.Select
                  className="form-select"
                  id="member_contactPreferencePost"
                  value={fields.member_contactPreferencePost}
                  onChange={handleFieldChange}
                  required
                >
                  {selectYesNo.map(keypairs => (
                    <option value={keypairs.id}>{keypairs.value}</option>
                  ))}
                </Form.Select>
                <Form.Label htmlFor="member_contactPreferencePost" className="form-label ">Post *</Form.Label>
              </div>

            </Col>
          </Row>
          <div class="form-floating mt-2">
            <Form.Control
              id="member_DOB"
              className="input required"
              placeholder="Date of Birth *"
              type="date"
              autoComplete="date"
              value={fields.member_DOB}
              onChange={handleFieldChange}
              required
            />
            <Form.Label htmlFor="member_DOB" className="form-label">Date of Birth *</Form.Label>
          </div>
        </div>
      </div>
    )
  }

  function getFormPageThree() {
    return (
      <div className="container mt-4 px-0">
        <h2>Page 3 of 7</h2>
        <div class="mt-2 form-floating">
          <Form.Control
            id="illnessInjury_what"
            className="textarea required textheight"
            placeholder="What is the illness / injury? *"
            value={fields.illnessInjury_what}
            onChange={handleFieldChange}
            required
            as="textarea"
            rows={3}
          />
          <Form.Label htmlFor="illnessInjury_what" className="form-label">What is the illness / injury? *</Form.Label>
        </div>

        <div class="mt-2 form-floating">
          <Form.Control
            id="illnessInjury_how"
            className="input required textheight"
            placeholder="How did it happen / occur? *"
            value={fields.illnessInjury_how}
            onChange={handleFieldChange}
            required
            as="textarea"
            rows={3}
          />
          <Form.Label htmlFor="illnessInjury_how" className="form-label">How did it happen / occur? *</Form.Label>
        </div>

        <div class="mt-2 form-floating">
          <Form.Control
            id="illnessInjury_prevent_working"
            className="input required textheight"
            placeholder="What symptoms have prevented you from performing your occupation? *"
            value={fields.illnessInjury_prevent_working}
            onChange={handleFieldChange}
            required
            as="textarea"
            rows={3}
          />
          <Form.Label htmlFor="illnessInjury_prevent_working" className="form-label">What symptoms have prevented you from performing your occupation? *</Form.Label>
        </div>

        <div class="mt-2">
          <Form.Label htmlFor="illnessInjury_when" className="form-label">When did your illness /injury commence? *</Form.Label>
          <Form.Control
            id="illnessInjury_when"
            className="input required"
            placeholder="When did your illness / injury commence? *"
            type="date"
            autoComplete="date"
            value={fields.illnessInjury_when}
            onChange={handleFieldChange}
            required
          />
        </div>

        <div class="mt-2 form-floating">
          <Form.Control
            id="illnessInjury_where"
            className="input required textheight"
            placeholder="Geographical location injury / illness occurred *"
            value={fields.illnessInjury_where}
            onChange={handleFieldChange}
            required
            as="textarea"
            aria-describedby="helpillnesInjury_where"
          />
          <Form.Label htmlFor="illnessInjury_where" className="form-label">Geographical location injury / illness occurred *</Form.Label>
          <Form.Text id="helpillnesInjury_where" muted>
            <i>Confirm the location e.g. city / town / country</i>
          </Form.Text>
        </div>

        <div class="mt-2 form-floating">
          <Form.Select
            className="input required"
            id="illnessInjury_history"
            value={fields.illnessInjury_history}
            onChange={handleFieldChange}
            required
          >
            {selectYesNo.map(keypairs => (
              <option value={keypairs.id}>{keypairs.value}</option>
            ))}
          </Form.Select>
          <Form.Label htmlFor="illnessInjury_history" className="form-label">Have you ever suffered from the illness or injury before? *</Form.Label>
        </div>

        <div class="mt-2 form-floating">
          <Form.Select
            className="input required"
            id="illnessInjury_consultation"
            value={fields.illnessInjury_consultation}
            onChange={handleFieldChange}
            required
          >
            {selectYesNo.map(keypairs => (
              <option value={keypairs.id}>{keypairs.value}</option>
            ))}
          </Form.Select>
          <Form.Label htmlFor="illnessInjury_consultation" className="form-label">Have you consulted a doctor / specialist? *</Form.Label>
        </div>

        <div class="input-group mt-2">
          <div class="form-floating">
            <Form.Control
              id="doctor_firstName"
              className="input required"
              placeholder="Your Doctor's First Name *"
              type="text"
              autoComplete=""
              value={fields.doctor_firstName}
              onChange={handleFieldChange}
              required
            />
            <Form.Label htmlFor="doctor_firstName" className="form-label">Your Doctor's First Name *</Form.Label>
          </div>

          <div class="form-floating">
            <Form.Control
              id="doctor_lastName"
              className="input required"
              placeholder="Your Doctor's Last Name *"
              type="text"
              autoComplete=""
              value={fields.doctor_lastName}
              onChange={handleFieldChange}
              required
            />
            <Form.Label htmlFor="doctor_lastName" className="form-label">Your Doctor's Last Name *</Form.Label>
          </div>
        </div>
        <div class="form-floating mt-2">
          <Form.Control
            id="doctor_telephone"
            className="input required"
            placeholder="Your Doctor's Telephone *"
            type="text"
            autoComplete=""
            value={fields.doctor_telephone}
            onChange={handleFieldChange}
            required
          />
          <Form.Label htmlFor="doctor_telephone" className="form-label">Your Doctor's Telephone *</Form.Label>
        </div>
        <div className="d-grid gap-2">
          <AddressPicker
            formTitle="Your Doctor's Address"
            id="doctor_CorrespondenceAddress"
            className="mb-2"
            handleFieldChange={handleFieldChange}
            postcode={fields.doctor_postcode}
            addressLine1={fields.doctor_addressLine1}
            addressLine2={fields.doctor_addressLine2}
            addressTown={fields.doctor_town}
            addressCounty={fields.doctor_county}
            addressCountry={fields.doctor_country}
            postcodeFieldId='doctor_postcode'
            addressLine1FieldId='doctor_addressLine1'
            addressLine2FieldId='doctor_addressLine2'
            addressTownFieldId='doctor_town'
            addressCountyFieldId='doctor_county'
            addressCountryFieldId='doctor_country'
            oldLayout={false}
          />
        </div>

        <div class="mt-2 form-floating">
          <Form.Select
            className="input required"
            id="doctor_changed"
            value={fields.doctor_changed}
            onChange={handleFieldChange}
            required
          >
            {selectYesNo.map(keypairs => (
              <option value={keypairs.id}>{keypairs.value}</option>
            ))}
          </Form.Select>
          <Form.Label htmlFor="doctor_changed" className="form-label">Have you changed your doctor within the last 12 months? *</Form.Label>
        </div>

        {
          fields.doctor_changed === "Yes" ? (
            <div>
              <div class="input-group">
                <div class="form-floating mt-2">
                  <Form.Control
                    id="doctor_changed_firstName"
                    className="input required"
                    placeholder="Your previous Doctor's First Name *"
                    type="text"
                    autoComplete=""
                    value={fields.doctor_changed_firstName}
                    onChange={handleFieldChange}
                    required
                  />
                  <Form.Label htmlFor="doctor_changed_firstName" className="form-label">Your previous Doctor's First Name *</Form.Label>
                </div>
                <div class="form-floating mt-2">
                  <Form.Control
                    id="doctor_changed_lastName"
                    className="input required"
                    placeholder="Your previous Doctor's Last Name *"
                    type="text"
                    autoComplete=""
                    value={fields.doctor_changed_lastName}
                    onChange={handleFieldChange}
                    required
                  />
                  <Form.Label htmlFor="doctor_changed_lastName" className="form-label">Your previous Doctor's Last Name *</Form.Label>
                </div>
              </div>
              <div class="form-floating mt-2">
                <Form.Control
                  id="doctor_changed_telephone"
                  className="input required"
                  placeholder="Your previous Doctor's Telephone *"
                  type="text"
                  autoComplete=""
                  value={fields.doctor_changed_telephone}
                  onChange={handleFieldChange}
                  required
                />
                <Form.Label htmlFor="doctor_changed_telephone" className="form-label">Your previous Doctor's telephone *</Form.Label>
              </div>

              <div className="d-grid gap-2">
                <AddressPicker
                  formTitle="Your Previous Doctor's Address"
                  id="doctor_changed_CorrespondenceAddress"
                  className="mb-2"
                  handleFieldChange={handleFieldChange}
                  postcode={fields.doctor_changed_postcode}
                  addressLine1={fields.doctor_changed_addressLine1}
                  addressLine2={fields.doctor_changed_addressLine2}
                  addressTown={fields.doctor_changed_town}
                  addressCounty={fields.doctor_changed_county}
                  addressCountry={fields.doctor_changed_country}
                  postcodeFieldId='doctor_changed_postcode'
                  addressLine1FieldId='doctor_changed_addressLine1'
                  addressLine2FieldId='doctor_changed_addressLine2'
                  addressTownFieldId='doctor_changed_town'
                  addressCountyFieldId='doctor_changed_county'
                  addressCountryFieldId='doctor_changed_country'
                  oldLayout={false}
                />
              </div>
            </div>
          ) : null
        }

        <div class="form-floating mt-2">
          <Form.Control
            id="illnessInjury_treatment"
            className="textheight required"
            placeholder="What treatment/medication have you received and do you anticipate further treatment? *"
            value={fields.illnessInjury_treatment}
            onChange={handleFieldChange}
            required
            as="textarea"
            rows={3}
          />
          <Form.Label htmlFor="illnessInjury_treatment" className="form-label">What treatment/medication have you received and do you anticipate further treatment? *</Form.Label>
        </div>

        <div class="form-floating mt-2">
          <Form.Select
            className="input required"
            id="illnessInjury_hospitalised"
            value={fields.illnessInjury_hospitalised}
            onChange={handleFieldChange}
            required
          >
            {selectYesNo.map(keypairs => (
              <option value={keypairs.id}>{keypairs.value}</option>
            ))}
          </Form.Select>
          <Form.Label htmlFor="illnessInjury_hospitalised" className="form-label">Have you been hospitalised in result of your injury / illness? *</Form.Label>
        </div>
      </div >
    )
  }

  function getFormPageFour() {
    return (
      <div className="container mt-4 px-0">
        <h2>Page 4 of 7</h2>

        <div class="form-floating mt-2">
          <Form.Select
            className="input required"
            id="occupation_main_origo"
            value={fields.occupation_main_origo}
            onChange={handleFieldChange}
            required
          >
            {
              Object.keys(origoList).map(
                origoId => (
                  <option value={origoId}>{origoList[origoId]}</option>
                )
              )
            }
          </Form.Select>
          <Form.Label htmlFor="occupation_main_origo" className="form-label">What is your main occupation? *</Form.Label>
        </div>
        {fields.occupation_main_origo !== "" ? (
          <div class="mt-2 form-floating">

            <Form.Control
              id="occupation_main_further"
              className="input"
              placeholder="If the description is not completely accurate, please confirm more information here"
              type="text"
              autoComplete=""
              value={fields.occupation_main_further}
              onChange={handleFieldChange}
            />
            <Form.Label htmlFor="occupation_main_further" className="form-label">If the description is not completely accurate, please confirm more information here</Form.Label>
          </div>
        ) : null}

        <div class="form-floating mt-2">
          <Form.Select
            className="input required"
            id="occupation_status"
            value={fields.occupation_status}
            onChange={handleFieldChange}
            required
          >
            {selectOccupationalStatus.map(keypairs => (
              <option value={keypairs.id}>{keypairs.value}</option>
            ))}
          </Form.Select>
          <Form.Label htmlFor="occupation_status" className="form-label">Occupation status *</Form.Label>
        </div>

        {employSelectCondition3.indexOf(fields.occupation_status) > -1 ? (
          <div>

            <div class="form-floating required mt-2">
              <Form.Control
                id="occupation_employer_name"
                className="input"
                placeholder="Employer Name *"
                type="text"
                autoComplete=""
                value={fields.occupation_employer_name}
                onChange={handleFieldChange}
                required
              />
              <Form.Label htmlFor="occupation_employer_name" className="form-label">Employer Name *</Form.Label>
            </div>

            <div class="form-floating required mt-2">
              <Form.Control
                id="occupation_employer_telephone"
                className="input"
                placeholder="Employer Telephone *"
                type="text"
                autoComplete=""
                value={fields.occupation_employer_telephone}
                onChange={handleFieldChange}
                required
              />
              <Form.Label htmlFor="occupation_employer_telephone" className="form-label">Employer Telephone *</Form.Label>
            </div>

            <div class="form-floating required mt-2">
              <Form.Control
                id="occupation_employer_email"
                className="input"
                placeholder="Employer Email *"
                type="text"
                autoComplete=""
                value={fields.occupation_employer_email}
                onChange={handleFieldChange}
                required
              />
              <Form.Label htmlFor="occupation_employer_email" className="form-label">Employer Email *</Form.Label>
            </div>

            <div className="d-grid gap-2">
              <AddressPicker
                formTitle="Your Employer's Address"
                id="occupation_CorrespondenceAddress"
                className="mb-2"
                handleFieldChange={handleFieldChange}
                postcode={fields.occupation_postcode}
                addressLine1={fields.occupation_addressLine1}
                addressLine2={fields.occupation_addressLine2}
                addressTown={fields.occupation_town}
                addressCounty={fields.occupation_county}
                addressCountry={fields.occupation_country}
                postcodeFieldId='occupation_postcode'
                addressLine1FieldId='occupation_addressLine1'
                addressLine2FieldId='occupation_addressLine2'
                addressTownFieldId='occupation_town'
                addressCountyFieldId='occupation_county'
                addressCountryFieldId='occupation_country'
                oldLayout={false}
              />
            </div>
          </div>
        ) : null}

        {employSelectCondition.indexOf(fields.occupation_status) > -1 ? (
          <div>
            <Form.Label htmlFor="occupation_further_accountantholdrecords" className="form-label">Does your accountant hold your financial records?</Form.Label>
            <Form.Select
              className="input"
              id="occupation_further_accountantholdrecords"
              value={fields.occupation_further_accountantholdrecords}
              onChange={handleFieldChange}
            >
              {selectYesNo.map(keypairs => (
                <option value={keypairs.id}>{keypairs.value}</option>
              ))}
            </Form.Select>

            {fields.occupation_further_accountantholdrecords === "Yes" ? (
              <div>
                <div class="form-floating required">
                  <Form.Control
                    id="occupation_accountant_name"
                    className="input"
                    placeholder="Accountant Name *"
                    type="text"
                    autoComplete=""
                    value={fields.occupation_accountant_name}
                    onChange={handleFieldChange}
                    required
                  />
                  <Form.Label htmlFor="occupation_accountant_name" className="form-label">Accountant Name *</Form.Label>
                </div>

                <div class="form-floating required">
                  <Form.Control
                    id="occupation_accountant_telephone"
                    className="input"
                    placeholder="Accountant Telephone *"
                    type="text"
                    autoComplete=""
                    value={fields.occupation_accountant_telephone}
                    onChange={handleFieldChange}
                    required
                  />
                  <Form.Label htmlFor="occupation_accountant_telephone" className="form-label">Accountant Telephone *</Form.Label>
                </div>

                <div class="form-floating required">
                  <Form.Control
                    id="occupation_accountant_email"
                    className="input"
                    placeholder="Accountant Email *"
                    type="text"
                    autoComplete=""
                    value={fields.occupation_accountant_email}
                    onChange={handleFieldChange}
                    required
                  />
                  <Form.Label htmlFor="occupation_accountant_email" className="form-label">Accountant Email *</Form.Label>
                </div>

                <div className="d-grid gap-2">
                  <AddressPicker
                    formTitle="Your Accountant's Address"
                    id="occupation_accountant_CorrespondenceAddress"
                    className="mb-2"
                    handleFieldChange={handleFieldChange}
                    postcode={fields.occupation_accountant_postcode}
                    addressLine1={fields.occupation_accountant_addressLine1}
                    addressLine2={fields.occupation_accountant_address_Line2}
                    addressTown={fields.occupation_accountant_town}
                    addressCounty={fields.occupation_accountant_county}
                    addressCountry={fields.occupation_accountant_country}
                    postcodeFieldId='occupation_accountant_postcode'
                    addressLine1FieldId='occupation_accountant_addressLine1'
                    addressLine2FieldId='occupation_accountant_address_Line2'
                    addressTownFieldId='occupation_accountant_town'
                    addressCountyFieldId='occupation_accountant_county'
                    addressCountryFieldId='occupation_accountant_country'
                    oldLayout={false}
                  />
                </div>

              </div>
            ) : null}

          </div>
        ) : null}

        <div class="form-floating mt-2">
          <Form.Select
            className="input required"
            id="occupation_2nd"
            value={fields.occupation_2nd}
            onChange={handleFieldChange}
            required
          >
            {selectYesNo.map(keypairs => (
              <option value={keypairs.id}>{keypairs.value}</option>
            ))}
          </Form.Select>
          <Form.Label htmlFor="occupation_2nd" className="form-label">Do you have a secondary occupation? *</Form.Label>
        </div>

        {fields.occupation_2nd === "Yes" ? (
          <div>

            <div class="form-floating mt-2">
              <Form.Select
                className="input required"
                id="occupation_2nd_origo"
                value={fields.occupation_2nd_origo}
                onChange={handleFieldChange}
                required
              >
                {
                  Object.keys(origoList).map(
                    origoId => (
                      <option value={origoId}>{origoList[origoId]}</option>
                    )
                  )
                }
              </Form.Select>
              <Form.Label htmlFor="occupation_2nd_origo" className="form-label">What is your secondary occupation? *</Form.Label>
            </div>

            {fields.occupation_2nd_origo !== "" ? (
              <div class="form-floating mt-2">
                <Form.Control
                  id="occupation_2nd_further"
                  className="input"
                  placeholder="If the description is not completely accurate, please confirm more information here *"
                  type="text"
                  autoComplete=""
                  value={fields.occupation_2nd_further}
                  onChange={handleFieldChange}
                />
                <Form.Label htmlFor="occupation_2nd_further" className="form-label">If the description is not completely accurate, please confirm more information here</Form.Label>

              </div>
            ) : null}
            <div class="form-floating mt-2">
              <Form.Select
                className="input required"
                id="occupation_2nd_status"
                value={fields.occupation_2nd_status}
                onChange={handleFieldChange}
                required
              >
                {selectOccupationalStatus.map(keypairs => (
                  <option value={keypairs.id}>{keypairs.value}</option>
                ))}
              </Form.Select>
              <Form.Label htmlFor="occupation_2nd_status" className="form-label">Secondary Occupation status *</Form.Label>
            </div>

            {employSelectCondition3.indexOf(fields.occupation_2nd_status) > -1 ? (
              <div>

                <div class="form-floating required mt-2">
                  <Form.Control
                    id="occupation_2nd_name"
                    className="input"
                    placeholder="Second Employer Name *"
                    type="text"
                    autoComplete=""
                    value={fields.occupation_2nd_name}
                    onChange={handleFieldChange}
                    required
                  />
                  <Form.Label htmlFor="occupation_2nd_name" className="form-label">Second Employer Name *</Form.Label>
                </div>

                <div class="form-floating required mt-2">
                  <Form.Control
                    id="occupation_2nd_telephone"
                    className="input"
                    placeholder="Second Employer Telephone *"
                    type="text"
                    autoComplete=""
                    value={fields.occupation_2nd_telephone}
                    onChange={handleFieldChange}
                    required
                  />
                  <Form.Label htmlFor="occupation_2nd_telephone" className="form-label">Second Employer Telephone *</Form.Label>
                </div>

                <div class="form-floating required mt-2">
                  <Form.Control
                    id="occupation_2nd_email"
                    className="input"
                    placeholder="Second Employer Email *"
                    type="text"
                    autoComplete=""
                    value={fields.occupation_2nd_email}
                    onChange={handleFieldChange}
                    required
                  />
                  <Form.Label htmlFor="occupation_2nd_email" className="form-label">Second Employer Email *</Form.Label>
                </div>

                <div className="d-grid gap-2">
                  <AddressPicker
                    formTitle="Your Second Employer's Address"
                    id="occupation_2nd_CorrespondenceAddress"
                    className="mb-2"
                    handleFieldChange={handleFieldChange}
                    postcode={fields.occupation_2nd_postcode}
                    addressLine1={fields.occupation_2nd_addressLine1}
                    addressLine2={fields.occupation_2nd_addressLine2}
                    addressTown={fields.occupation_2nd_town}
                    addressCounty={fields.occupation_2nd_county}
                    addressCountry={fields.occupation_2nd_country}
                    postcodeFieldId='occupation_2nd_postcode'
                    addressLine1FieldId='occupation_2nd_addressLine1'
                    addressLine2FieldId='occupation_2nd_addressLine2'
                    addressTownFieldId='occupation_2nd_town'
                    addressCountyFieldId='occupation_2nd_county'
                    addressCountryFieldId='occupation_2nd_country'
                    oldLayout={false}
                  />
                </div>
              </div>
            ) : null}

            {employSelectCondition.indexOf(fields.occupation_2nd_status) > -1 ? (
              <div>
                <div class="form-floating mt-2">
                  <Form.Select
                    className="input required"
                    id="occupation_2nd_further_accountantholdrecords"
                    value={fields.occupation_2nd_further_accountantholdrecords}
                    onChange={handleFieldChange}
                    required
                  >
                    {selectYesNo.map(keypairs => (
                      <option value={keypairs.id}>{keypairs.value}</option>
                    ))}
                  </Form.Select>
                  <Form.Label htmlFor="occupation_2nd_further_accountantholdrecords" className="form-label">Does your accountant hold your financial records? *</Form.Label>
                </div>

                {fields.occupation_2nd_further_accountantholdrecords === "Yes" ? (
                  <div>
                    <div class="form-floating required mt-2">
                      <Form.Control
                        id="occupation_2nd_accountant_name"
                        className="input"
                        placeholder="Second Accountant Name *"
                        type="text"
                        autoComplete=""
                        value={fields.occupation_2nd_accountant_name}
                        onChange={handleFieldChange}
                        required
                      />
                      <Form.Label htmlFor="occupation_2nd_accountant_name" className="form-label">Second Accountant Name *</Form.Label>
                    </div>

                    <div class="form-floating required mt-2">
                      <Form.Control
                        id="occupation_2nd_accountant_telephone"
                        className="input"
                        placeholder="Accountant Telephone *"
                        type="text"
                        autoComplete=""
                        value={fields.occupation_2nd_accountant_telephone}
                        onChange={handleFieldChange}
                        required
                      />
                      <Form.Label htmlFor="occupation_2nd_accountant_telephone" className="form-label">Second Accountant Telephone *</Form.Label>
                    </div>

                    <div class="form-floating required mt-2">
                      <Form.Control
                        id="occupation_2nd_accountant_email"
                        className="input"
                        placeholder="Second Accountant Email *"
                        type="text"
                        autoComplete=""
                        value={fields.occupation_2nd_accountant_email}
                        onChange={handleFieldChange}
                        required
                      />
                      <Form.Label htmlFor="occupation_2nd_accountant_email" className="form-label">Second Accountant Email *</Form.Label>
                    </div>

                    <div className="d-grid gap-2">
                      <AddressPicker
                        formTitle="Your Second Accountant's Address"
                        id="occupation_2nd_accountant_CorrespondenceAddress"
                        className="mb-2"
                        handleFieldChange={handleFieldChange}
                        postcode={fields.occupation_2nd_accountant_postcode}
                        addressLine1={fields.occupation_2nd_accountant_addressLine1}
                        addressLine2={fields.occupation_2nd_accountant_addressLine2}
                        addressTown={fields.occupation_2nd_accountant_town}
                        addressCounty={fields.occupation_2nd_accountant_county}
                        addressCountry={fields.occupation_2nd_accountant_country}
                        postcodeFieldId='occupation_2nd_accountant_postcode'
                        addressLine1FieldId='occupation_2nd_accountant_addressLine1'
                        addressLine2FieldId='occupation_2nd_accountant_addressLine2'
                        addressTownFieldId='occupation_2nd_accountant_town'
                        addressCountyFieldId='occupation_2nd_accountant_county'
                        addressCountryFieldId='occupation_2nd_accountant_country'
                        oldLayout={false}
                      />
                    </div>

                  </div>
                ) : null}

              </div>
            ) : null}

            <div class="form-floating required mt-2">
              <Form.Select
                className="input required"
                id="occupation_more"
                value={fields.occupation_more}
                onChange={handleFieldChange}
                required
              >
                {selectYesNo.map(keypairs => (
                  <option value={keypairs.id}>{keypairs.value}</option>
                ))}
              </Form.Select>
              <Form.Label htmlFor="occupation_more" className="form-label">Do you have more than two occupations? *</Form.Label>
            </div>
          </div>
        ) : null}

        <div class="form-floating mt-2">
          <Form.Select
            className="input required"
            id="occupation_changed12Months"
            value={fields.occupation_changed12Months}
            onChange={handleFieldChange}
            required
          >
            {selectYesNo.map(keypairs => (
              <option value={keypairs.id}>{keypairs.value}</option>
            ))}
          </Form.Select>
          <Form.Label htmlFor="occupation_changed12Months" className="form-label">Have your employment circumstances changed in the last 12 months? *</Form.Label>
        </div>
      </div>
    )
  }

  function getFormPageFive() {
    return (
      <div className="container mt-4 px-0">
        <h2>Page 5 of 7</h2>
        <p>Will you receive income from any of the following as a result of your illness or injury?</p>
        <Form.Label htmlFor="benefit_continuedEarningsSSP" className="form-label">Continuing earnings from your occupations including Statutory Sick Pay? *</Form.Label>
        <Form.Select
          className="input"
          id="benefit_continuedEarningsSSP"
          value={fields.benefit_continuedEarningsSSP}
          onChange={handleFieldChange}
        >
          {selectYesNo.map(keypairs => (
            <option value={keypairs.id}>{keypairs.value}</option>
          ))}
        </Form.Select>
        <Form.Label htmlFor="benefit_EarlyRetirement" className="form-label">Early retirement pension through ill health? *</Form.Label>
        <Form.Select
          className="input"
          id="benefit_EarlyRetirement"
          value={fields.benefit_EarlyRetirement}
          onChange={handleFieldChange}
        >
          {selectYesNo.map(keypairs => (
            <option value={keypairs.id}>{keypairs.value}</option>
          ))}
        </Form.Select>
        <Form.Label htmlFor="benefit_workPensions" className="form-label">Regular Government financial support? *</Form.Label>
        <Form.Select
          className="input"
          id="benefit_workPensions"
          value={fields.benefit_workPensions}
          onChange={handleFieldChange}
        >
          {selectYesNo.map(keypairs => (
            <option value={keypairs.id}>{keypairs.value}</option>
          ))}
        </Form.Select>
        <Form.Label htmlFor="benefit_otherInsurance" className="form-label">Regular payments from other insurance policies resulting from your illness/injury? *</Form.Label>
        <Form.Select
          className="input"
          id="benefit_otherInsurance"
          value={fields.benefit_otherInsurance}
          onChange={handleFieldChange}
        >
          {selectYesNo.map(keypairs => (
            <option value={keypairs.id}>{keypairs.value}</option>
          ))}
        </Form.Select>

        <p>* Please ensure the above information is answered accurately as it may impact your claim</p>
      </div>
    )
  }

  function getFormPageSix() {
    return (
      <div className="container mt-4 px-0">
        <h2>Page 6 of 7</h2>
        <Form onSubmit={handleSubmit} className='my-2'>
          <div>
            <p>Bank Account Details</p>
            <div class="form-floating mt-2 required">
              <Form.Select
                className="input"
                id="payments"
                value={fields.payments}
                onChange={handleFieldChange}
                required
                aria-describedby='helpPayments'
              >
                {selectBankAccount.map(keypairs => (
                  <option value={keypairs.id}>{keypairs.value}</option>
                ))}
              </Form.Select>
              <Form.Label htmlFor="payments" className="form-label">If your claim is successful, where would you like your payments to be made? *</Form.Label>
              <Form.Text id="helpPayments" muted>
                <i>If selecting 'A different account', we will contact you to obtain further details</i>
              </Form.Text>
            </div>

            <p class="mt-2"><strong>* Please note we are unable to make payments into business bank accounts or any account that is not in your name.</strong></p>

            <div class="form-floating mt-2">
              <Form.Select
                className="input"
                id="furtherInformation"
                value={fields.furtherInformation}
                onChange={handleFieldChange}
                required
              >
                {selectYesNo.map(keypairs => (
                  <option value={keypairs.id}>{keypairs.value}</option>
                ))}
              </Form.Select>
              <Form.Label htmlFor="furtherInformation" className="form-label">Is there any further information you wish to provide us to support your claim? *</Form.Label>
            </div>

            {fields.furtherInformation === "Yes" ? (
              <div>
                <Form.Label htmlFor="furtherInformation_more" className="form-label visually-hidden">Please provide more detail. *</Form.Label>
                <Form.Control
                  id="furtherInformation_more"
                  className=""
                  placeholder="Please provide more detail. *"
                  value={fields.furtherInformation_more}
                  onChange={handleFieldChange}
                  required
                  as="textarea"
                  rows={3}
                />
              </div>
            ) : null}

            <div class="form-floating mt-2 required">
              <Form.Select
                className="input"
                id="discussClaimOthers"
                value={fields.discussClaimOthers}
                onChange={handleFieldChange}
                required
              >
                {selectYesNo.map(keypairs => (
                  <option value={keypairs.id}>{keypairs.value}</option>
                ))}
              </Form.Select>
              <Form.Label htmlFor="discussClaimOthers" className="form-label">Would you like to give anyone else permission to discuss your claim on your behalf? *</Form.Label>
            </div>

            {fields.discussClaimOthers === "Yes" ? (
              <div>
                <Form.Label htmlFor="discussClaimOthers_more" className="form-label visually-hidden">Please confirm name and relationship. *</Form.Label>
                <Form.Control
                  id="discussClaimOthers_more"
                  className="textheight required"
                  placeholder="Please provide more detail. Please confirm name and relationship. *"
                  value={fields.discussClaimOthers_more}
                  onChange={handleFieldChange}
                  required
                  as="textarea"
                  rows={3}
                />
              </div>
            ) : null}
          </div>
        </Form>
      </div>
    )
  }

  function getFormPageSeven() {
    return (
      <div className="container mt-4 px-0">
        <h2>Page 7 of 7</h2>
        <Form onSubmit={handleSubmit} className='my-2'>
          {displayAnswersBack()}

          <div class="row m-5 p-4 border border-primary-subtle">
            <div class="col">
              <Form.Group>
                <Form.Label htmlFor="confirmation" className="form-label visually-hidden">I confirm the answers provided are true and accurate. *</Form.Label>
                <Form.Check
                  id="confirmation"
                  className=""
                  type="checkbox"
                  checked={fields.confirmation}
                  value={fields.confirmation}
                  onChange={handleFieldChange}
                  label="I confirm the answers provided are true and accurate."
                  required
                />
              </Form.Group>
            </div>
            <div class="col">
              {getCaptcha()}
            </div>
          </div>

          <div>
            <p>If you are happy with your answers, please click 'Submit' to enable an assessment to begin.</p>
          </div>

          <LoaderButton
            disabled={!isValid}
            size="large"
            type="submit"
            isLoading={isLoading}
          >
            Submit claim
          </LoaderButton>
        </Form>
      </div>
    )
  }

  function getForm() {
    return (
      <TabbedContainer
        titles={
          ['Important Information', 'Your Details', 'Your Claim', 'Occupation', 'Your Earnings', 'Additional Information', 'Review and submit']
        }
        children={[
          getFormPageOne(),
          getFormPageTwo(),
          getFormPageThree(),
          getFormPageFour(),
          getFormPageFive(),
          getFormPageSix(),
          getFormPageSeven(),
        ]}
        showNextAndPreviousTop={false}
        showNextAndPreviousBottom={true}
        // Callback function that will be called just before tab is changed.
        // Will be passed two arguments, index navigated from, and index navigated to.
        // Returns an array of two boolean values
        // [currentTabIsValid, preventTabChange]
        // currentTabIsValid will be used to track the highest valid tab, which only
        // allows the user to click forward through tabs they ahve already completed.
        // preventTabChange will block the tab change is set to false.
        onBeforeTabChange={(from, to) => {
          if (to > from) {
            // When trying to move to the next tab, prevent tab change unless
            // the current tab is valid. Scroll to the top to show any validation
            // errors.
            const isValid = validateActivePage(true, true);
            const preventTabChange = !isValid;
            return ([isValid, preventTabChange]);
          }
          else {
            // When going back to a lower tab, still validate, but don't scroll
            // to the top or display them if there are errors. Do give the
            // result to the tabbed container for it to track highestValidTab.
            const isValid = validateActivePage(false, false);
            // You can always go bckwards
            const preventTabChange = false;
            return ([isValid, preventTabChange]);
          }
        }}
        onAfterTabChange={(from, to) => {
          setActivePage(to)
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        trackEvents={true}
        eventCategory='submitClaim'
      />
    )
  }

  /**
   * Returns the message to show once the formdata has been submitted.
   */
  function getSubmittedMessage() {
    return (
      <Alert variant="primary">
        <p>Thank you for submitting your online claim form. We aim to contact you within 5 working days to confirm what happens next.</p>
        <p>For further information, please <a target="_blank" rel="noreferrer" href={claimsDocLink}>click here for 'our guide to claims'</a></p>
      </Alert>
    )
  }

  function getPage() {
    return(
      <div className="largeform">
        <h1 className="mb-3 font-weight-normal text-center display-1">Submit a claim</h1>
        {isSubmitted ? getSubmittedMessage() : null}
        {isSubmitted ? null : getForm()}
      </div>
    );
  }


  function getDownPage() {
    return(
    <Alert variant="primary">
      <p>This page is temporarily unavailable, please check back later.</p>
      <p>
        In the meantime, you can call us on <a href="tel:+800-587-5098" class="text-decoration-underline">0800 587 5098</a> or email us at <a href="mailto:claims@cirencester-friendly.co.uk">claims@cirencester-friendly.co.uk</a>. A member of our <strong>Claims Team</strong> will be happy to help.
      </p>
    </Alert>
    )
  }

  return (
    <Container fluid="md">
      {isTemporarilyDown ? getDownPage() : getPage()}
    </Container>
  );
}
